import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge as BadgeType } from '@communitly/shared';
import { BadgeIconPicker } from './BadgeIconPicker';
import { BadgeColorPicker } from './BadgeColorPicker';
import { getIconComponent } from '@/utils/iconUtils';
import { AlertTriangle, Loader2, Eye } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { useCommunity } from '@/contexts/CommunityContext';

interface BadgeFormProps {
  badge?: BadgeType | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
}

interface BadgeFormData {
  name: string;
  description: string;
  color: string;
  icon: string;
  is_active: boolean;
  translation_key: string;
}

// Generate translation key from name
const generateTranslationKey = (name: string): string => {
  return `badge.${name.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '')}`;
};

export const BadgeForm: React.FC<BadgeFormProps> = ({
  badge,
  isOpen,
  onOpenChange,
  mode
}) => {
  const { currentCommunity } = useCommunity();
  const queryClient = useQueryClient();
  const isSystemBadge = badge?.is_system_badge || false;
  const isEditing = mode === 'edit';

  // Completely block system badge editing
  if (isEditing && isSystemBadge) {
    return null;
  }

  // Form state
  const [formData, setFormData] = useState<BadgeFormData>({
    name: '',
    description: '',
    color: '#f97316',
    icon: 'Star',
    is_active: true,
    translation_key: ''
  });

  // Initialize form data when badge changes
  useEffect(() => {
    if (badge && isEditing) {
      setFormData({
        name: badge.name || '',
        description: badge.description || '',
        color: badge.color || '#f97316',
        icon: badge.icon || 'Star',
        is_active: badge.is_active ?? true,
        translation_key: badge.translation_key || ''
      });
    } else if (!isEditing) {
      // Reset for create mode
      setFormData({
        name: '',
        description: '',
        color: '#f97316',
        icon: 'Star',
        is_active: true,
        translation_key: ''
      });
    }
  }, [badge, isEditing, isOpen]);

  // Update translation key when name changes (for new badges)
  useEffect(() => {
    if (!isEditing && formData.name) {
      setFormData(prev => ({
        ...prev,
        translation_key: generateTranslationKey(formData.name)
      }));
    }
  }, [formData.name, isEditing]);

  // Create badge mutation
  const createBadgeMutation = useMutation({
    mutationFn: async (data: BadgeFormData) => {
      // First create the translation default
      const { error: translationError } = await supabase
        .from('translation_defaults')
        .insert({
          key: data.translation_key,
          default_value: data.name,
          description: `Translation for ${data.name} badge`
        });

      if (translationError && !translationError.message.includes('duplicate key')) {
        throw translationError;
      }

      // Then create the badge
      const { data: newBadge, error } = await supabase
        .from('badges')
        .insert({
          name: data.name,
          description: data.description,
          color: data.color,
          icon: data.icon,
          is_active: data.is_active,
          is_system_badge: false,
          community_id: currentCommunity?.id,
          translation_key: data.translation_key
        })
        .select()
        .single();

      if (error) throw error;
      return newBadge;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['badges'] });
      toast({
        title: "Success",
        description: "Badge created successfully",
      });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to create badge",
      });
    },
  });

  // Update badge mutation
  const updateBadgeMutation = useMutation({
    mutationFn: async (data: BadgeFormData) => {
      if (!badge) throw new Error('No badge to update');

      // Update translation default if name changed
      if (badge.translation_key && data.name !== badge.name) {
        const { error: translationError } = await supabase
          .from('translation_defaults')
          .update({
            default_value: data.name,
            description: `Translation for ${data.name} badge`
          })
          .eq('key', badge.translation_key);

        if (translationError) {
          console.warn('Failed to update translation:', translationError);
        }
      }

      // Update the badge (only allow certain fields for system badges)
      const updateData = isSystemBadge 
        ? { 
            description: data.description,
            is_active: data.is_active 
          }
        : {
            name: data.name,
            description: data.description,
            color: data.color,
            icon: data.icon,
            is_active: data.is_active
          };

      const { data: updatedBadge, error } = await supabase
        .from('badges')
        .update(updateData)
        .eq('id', badge.id)
        .select()
        .single();

      if (error) throw error;
      return updatedBadge;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['badges'] });
      toast({
        title: "Success",
        description: "Badge updated successfully",
      });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to update badge",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isEditing) {
      updateBadgeMutation.mutate(formData);
    } else {
      createBadgeMutation.mutate(formData);
    }
  };

  const isLoading = createBadgeMutation.isPending || updateBadgeMutation.isPending;
  const PreviewIcon = getIconComponent(formData.icon);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Badge' : 'Create Badge'}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Modify the badge details below.' 
              : 'Create a new badge for highlighting businesses.'
            }
          </DialogDescription>
        </DialogHeader>

        {isSystemBadge && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>System Badge</AlertTitle>
            <AlertDescription>
              This is a system badge. Only description and status can be modified.
              Name, icon, and color are managed globally.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Badge Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              disabled={isSystemBadge}
              required
              placeholder="Enter badge name..."
            />
          </div>

          {/* Translation Key (readonly, for reference) */}
          {formData.translation_key && (
            <div className="space-y-2">
              <Label htmlFor="translation_key">Translation Key</Label>
              <Input
                id="translation_key"
                value={formData.translation_key}
                disabled
                className="text-muted-foreground"
              />
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter badge description..."
              rows={3}
            />
          </div>

          {/* Icon Picker */}
          <div className="space-y-2">
            <Label>Icon</Label>
            <BadgeIconPicker
              value={formData.icon}
              onChange={(icon) => setFormData(prev => ({ ...prev, icon }))}
              disabled={isSystemBadge}
            />
          </div>

          {/* Color Picker */}
          <div className="space-y-2">
            <Label>Color</Label>
            <BadgeColorPicker
              value={formData.color}
              onChange={(color) => setFormData(prev => ({ ...prev, color }))}
              disabled={isSystemBadge}
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
            />
            <Label htmlFor="is_active">Active</Label>
          </div>

          {/* Live Preview */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Preview
            </Label>
            <div className="p-4 border rounded-lg bg-muted/30">
              <div className="flex items-center gap-3">
                <div 
                  className="flex items-center justify-center w-8 h-8 rounded-full"
                  style={{ backgroundColor: formData.color + '20' }}
                >
                  <PreviewIcon 
                    className="w-4 h-4"
                    style={{ color: formData.color }}
                  />
                </div>
                <div>
                  <div className="font-medium">
                    {formData.name || 'Badge Name'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {formData.description || 'Badge description...'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            onClick={handleSubmit}
            disabled={isLoading || !formData.name.trim()}
          >
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEditing ? 'Update Badge' : 'Create Badge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};